import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DateCell, MutedCell, NameCell, NumberCell } from '@/components/TableCells'
import { StatusPill } from '@/components/DeliveryStatusBadge'
import { AttentionCard, type AttentionTab } from '@/components/AttentionCard'
import { useNetwork } from '../contexts/NetworkContext'
import { getContext } from '@/services/networks/networksApi'
import { AuthService } from '@/services/core/auth'
import { AdUnitsApi } from '@/services/adUnits/adUnitsApi'
import { StatsApi } from '@/services/stats/statsApi'
import { readStatsResult } from '@/services/stats/statsQueryResult'
import { formatPercent } from '../utils/format'

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS
/** Days the last 24 hours are compared against. */
const BASELINE_DAYS = 7
/** A drop to less than this share of the usual daily requests is flagged. */
const DROP_RATIO = 0.5
/** Fill rates under this percentage are flagged. */
const LOW_FILL_PCT = 20
/**
 * Requests a day an ad unit needs before its traffic or fill is judged — below
 * it, a swing is noise.
 */
const MIN_DAILY_REQUESTS = 1000

/** One ad unit, with whatever the tab it is listed on knows about it. */
interface AdUnitRow {
  id: string
  name: string
  siteName?: string
  earningsAccountName?: string
  createTime?: string
  /** Requests in the last 24 hours. */
  requests24h?: number
  /** Average requests a day over the baseline week. */
  dailyAverage?: number
  /** Change of the last 24 hours against the daily average, in percent. */
  change?: number
  /** Requests over the baseline week and the last day together. */
  requests?: number
  impressions?: number
  fillRate?: number
}

interface AdUnitTraffic {
  name: string
  requests: number
  impressions: number
}

/** Requests and impressions per ad unit over a period, keyed by ad unit id. */
async function fetchTraffic(startDate: string, endDate: string, currency: string) {
  const response = await StatsApi.get({
    startDate,
    endDate,
    currency,
    groupBy: 'AD_UNIT',
    all: 'adUnit',
    timeout: 180000,
  })
  const result = readStatsResult(response, 'AD_UNIT', null)
  const traffic = new Map<string, AdUnitTraffic>()
  for (const row of result.rows) {
    if (!row.objectId) continue
    traffic.set(row.objectId, {
      name: row.name,
      requests: Number(row.stats.impressionRequests) || 0,
      impressions: Number(row.stats.impressions) || 0,
    })
  }
  return traffic
}

/**
 * The ad units someone should look at today: ones that get no requests at
 * all, ones whose traffic has fallen sharply against the week before, and
 * busy ones that rarely fill.
 */
export function AdUnitAttentionCard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentNetwork, scopeReady } = useNetwork()
  const enabled = AuthService.isAuthenticated() && scopeReady && !!currentNetwork
  const context = getContext()
  const currency = currentNetwork?.defaultCurrency ?? 'USD'

  const unrequested = useQuery({
    queryKey: ['adUnits', 'attention', 'unrequested', context],
    queryFn: () => AdUnitsApi.getUnrequested(),
    enabled,
  })

  // The last 24 hours, and the week before it, ending on the hour so the
  // query key stays put between renders.
  const periods = useMemo(() => {
    const end = Math.floor(Date.now() / HOUR_MS) * HOUR_MS
    const iso = (ms: number) => new Date(ms).toISOString()
    return {
      recent: { startDate: iso(end - DAY_MS), endDate: iso(end) },
      baseline: { startDate: iso(end - (BASELINE_DAYS + 1) * DAY_MS), endDate: iso(end - DAY_MS) },
    }
  }, [])

  const recent = useQuery({
    queryKey: ['stats', 'adUnitAttention', 'recent', context, periods.recent],
    queryFn: () => fetchTraffic(periods.recent.startDate, periods.recent.endDate, currency),
    enabled,
  })

  const baseline = useQuery({
    queryKey: ['stats', 'adUnitAttention', 'baseline', context, periods.baseline],
    queryFn: () => fetchTraffic(periods.baseline.startDate, periods.baseline.endDate, currency),
    enabled,
  })

  const lists = useMemo(() => {
    const noTraffic: AdUnitRow[] = (unrequested.data?.results ?? []).map((adUnit) => ({
      id: adUnit.id,
      name: adUnit.name,
      siteName: adUnit.site?.name,
      earningsAccountName: adUnit.earningsAccountName,
      createTime: adUnit.createTime,
    }))

    const trafficDrop: AdUnitRow[] = []
    const lowFill: AdUnitRow[] = []
    if (recent.data && baseline.data) {
      for (const [id, week] of baseline.data) {
        const dailyAverage = week.requests / BASELINE_DAYS
        if (dailyAverage < MIN_DAILY_REQUESTS) continue
        const day = recent.data.get(id)
        const requests24h = day?.requests ?? 0
        if (requests24h < dailyAverage * DROP_RATIO) {
          trafficDrop.push({
            id,
            name: week.name,
            requests24h,
            dailyAverage,
            change: ((requests24h - dailyAverage) / dailyAverage) * 100,
          })
        }
        const requests = week.requests + requests24h
        const impressions = week.impressions + (day?.impressions ?? 0)
        const fillRate = (impressions / requests) * 100
        if (fillRate < LOW_FILL_PCT) {
          lowFill.push({ id, name: week.name, requests, impressions, fillRate })
        }
      }
    }

    return {
      noTraffic,
      trafficDrop: trafficDrop.sort((a, b) => (a.change ?? 0) - (b.change ?? 0)),
      lowFill: lowFill.sort((a, b) => (a.fillRate ?? 0) - (b.fillRate ?? 0)),
    }
  }, [unrequested.data, recent.data, baseline.data])

  const tabs = useMemo<AttentionTab<AdUnitRow>[]>(() => {
    const statsLoading = recent.isLoading || baseline.isLoading

    const name = (withSite: boolean): ColumnDef<AdUnitRow, unknown> => ({
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 160 },
      cell: ({ row }) => (
        <NameCell name={row.original.name} sub={withSite ? row.original.siteName : undefined} />
      ),
    })
    const count = (field: 'requests24h' | 'dailyAverage' | 'requests' | 'impressions', header: string) =>
      ({
        accessorKey: field,
        header,
        size: 140,
        cell: ({ row }) => <NumberCell value={row.original[field] ?? null} />,
      }) satisfies ColumnDef<AdUnitRow, unknown>

    return [
      {
        key: 'noTraffic',
        label: t('publishingHome.tabs.noTraffic'),
        hint: unrequested.data?.truncated
          ? t('publishingHome.truncated', { count: lists.noTraffic.length })
          : t('publishingHome.hints.noTraffic'),
        empty: t('publishingHome.empty.noTraffic'),
        rows: lists.noTraffic,
        loading: unrequested.isLoading,
        columns: [
          name(true),
          {
            accessorKey: 'earningsAccountName',
            header: t('publishingHome.earningsAccount'),
            meta: { minWidth: 150 },
            cell: ({ row }) => <MutedCell value={row.original.earningsAccountName} />,
          },
          {
            accessorKey: 'createTime',
            header: t('common.created'),
            size: 120,
            cell: ({ row }) => <DateCell value={row.original.createTime} />,
          },
        ],
      },
      {
        key: 'trafficDrop',
        label: t('publishingHome.tabs.trafficDrop'),
        hint: t('publishingHome.hints.trafficDrop', {
          days: BASELINE_DAYS,
          ratio: Math.round((1 - DROP_RATIO) * 100),
        }),
        empty: t('publishingHome.empty.trafficDrop'),
        rows: lists.trafficDrop,
        loading: statsLoading,
        columns: [
          name(false),
          count('requests24h', t('publishingHome.requests24h')),
          count('dailyAverage', t('publishingHome.dailyAverage', { days: BASELINE_DAYS })),
          {
            accessorKey: 'change',
            header: t('publishingHome.change'),
            size: 120,
            cell: ({ row }) => (
              <StatusPill tone={(row.original.change ?? 0) <= -90 ? 'red' : 'amber'}>
                {formatPercent(row.original.change ?? 0, 0)}
              </StatusPill>
            ),
          },
        ],
      },
      {
        key: 'lowFill',
        label: t('publishingHome.tabs.lowFill'),
        hint: t('publishingHome.hints.lowFill', { days: BASELINE_DAYS + 1, value: LOW_FILL_PCT }),
        empty: t('publishingHome.empty.lowFill'),
        rows: lists.lowFill,
        loading: statsLoading,
        columns: [
          name(false),
          count('requests', t('stats.impressionRequests')),
          count('impressions', t('stats.impressions')),
          {
            accessorKey: 'fillRate',
            header: t('publishingHome.fillRate'),
            size: 120,
            cell: ({ row }) => (
              <StatusPill tone={(row.original.fillRate ?? 0) < LOW_FILL_PCT / 2 ? 'red' : 'amber'}>
                {formatPercent(row.original.fillRate ?? 0, 1)}
              </StatusPill>
            ),
          },
        ],
      },
    ]
  }, [t, lists, unrequested.data, unrequested.isLoading, recent.isLoading, baseline.isLoading])

  return (
    <AttentionCard
      tabs={tabs}
      storageKey="adUnits"
      onRowClick={(row) => navigate(`/publishing/ad-units/${row.id}`)}
      viewAllHref="/publishing/ad-units"
    />
  )
}
